import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { kioskApi, loadKioskCtx, formatKioskError, clearKioskDevice } from "@/lib/kioskApi";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTheme } from "@/components/ThemeProvider";
import {
  Loader2, Building2, RefreshCcw, ArrowLeft, Delete, CheckCircle2, XCircle,
  AlertTriangle, Clock, LogOut, UserRound, Play, Square,
} from "lucide-react";

const IDLE_MS = 60_000; // 60s inactivity → back to roster

/**
 * Public tablet kiosk shell. Views: "roster" → "pin" → "confirm" → "result".
 * Device JWT (IP-bound) authenticates every request; worker session token
 * is minted on PIN verify and lives 90s server-side (single-use per action).
 */
export default function KioskShell() {
  const nav = useNavigate();
  const { theme } = useTheme();
  const brand = theme?.product_display_name || "Quanta";
  const [ctx, setCtx] = useState(null); // {tenant, worksite, device, config, workers, dimensions}
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [view, setView] = useState("roster"); // roster | pin | confirm | result
  const [selected, setSelected] = useState(null); // worker (with open_state)
  const [worker, setWorker] = useState(null); // worker after PIN success (state includes token, current_state)
  const [result, setResult] = useState(null); // {kind: "in"|"out"|"switch", entry}

  const refreshSession = useCallback(async () => {
    setErr("");
    try {
      const { data } = await kioskApi.get("/kiosk/session");
      setCtx(data);
    } catch (e) {
      setErr(formatKioskError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  // Boot: if no device JWT → pair page. Otherwise load session.
  useEffect(() => {
    const tok = localStorage.getItem("clockin.kiosk_device");
    if (!tok) {
      nav("/kiosk/pair", { replace: true });
      return;
    }
    // Optimistic ctx from localStorage before network
    const cached = loadKioskCtx();
    if (cached) setCtx((c) => c || { ...cached, workers: [], dimensions: [] });
    refreshSession();
  }, [nav, refreshSession]);

  // Auto-refresh roster every 30s so open-punch states stay honest
  useEffect(() => {
    const t = setInterval(() => {
      if (view === "roster") refreshSession();
    }, 30_000);
    return () => clearInterval(t);
  }, [view, refreshSession]);

  // Reset to roster after idle (only when not on roster)
  const idleTimer = useRef(null);
  const kickIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (view === "roster") return;
    idleTimer.current = setTimeout(() => {
      setView("roster");
      setSelected(null);
      setWorker(null);
      setResult(null);
    }, IDLE_MS);
  }, [view]);
  useEffect(() => {
    kickIdle();
    const evts = ["mousedown", "touchstart", "keydown"];
    evts.forEach((e) => window.addEventListener(e, kickIdle));
    return () => {
      evts.forEach((e) => window.removeEventListener(e, kickIdle));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [kickIdle]);

  const backToRoster = () => {
    setView("roster");
    setSelected(null);
    setWorker(null);
    setResult(null);
    refreshSession();
  };

  const unpair = () => {
    if (!window.confirm("Unpair this tablet and return to the pairing screen?")) return;
    clearKioskDevice();
    nav("/kiosk/pair", { replace: true });
  };

  if (loading && !ctx) {
    return (
      <div className="brand-navy-gradient flex min-h-screen items-center justify-center text-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="brand-navy-gradient min-h-screen text-white" data-testid="kiosk-shell">
      <KioskHeader ctx={ctx} onRefresh={refreshSession} onUnpair={unpair} />

      <main className="mx-auto max-w-6xl px-8 pb-16">
        {err && (
          <Alert variant="destructive" className="mb-6 border-red-400/40 bg-red-500/10 text-red-100" data-testid="kiosk-error">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        {view === "roster" && (
          <RosterView
            ctx={ctx}
            brand={brand}
            onPick={(w) => {
              setSelected(w);
              setWorker(null);
              setResult(null);
              setView("pin");
            }}
          />
        )}

        {view === "pin" && selected && (
          <PinView
            ctx={ctx}
            worker={selected}
            onCancel={backToRoster}
            onSuccess={(payload) => {
              setWorker({ ...payload.worker, token: payload.worker_session_token });
              setView("confirm");
            }}
          />
        )}

        {view === "confirm" && worker && (
          <ConfirmView
            ctx={ctx}
            worker={worker}
            onBack={backToRoster}
            onDone={(kind, entry) => {
              setResult({ kind, entry });
              setView("result");
            }}
          />
        )}

        {view === "result" && result && (
          <ResultView result={result} worker={worker} onDone={backToRoster} />
        )}
      </main>
    </div>
  );
}

/* ---------- Header ---------- */

function KioskHeader({ ctx, onRefresh, onUnpair }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const tz = ctx?.tenant ? undefined : undefined; // rendered in device local
  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  return (
    <header className="border-b border-white/5 bg-white/[0.02] backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-8 py-5">
        <div className="flex items-center gap-4">
          <Logo variant="dark" />
          <div className="hidden h-8 w-px bg-white/10 sm:block" />
          <div className="hidden sm:block">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/50" data-testid="kiosk-tenant-name">
              {ctx?.tenant?.name || "—"}
            </div>
            <div className="flex items-center gap-2 text-lg font-semibold" data-testid="kiosk-worksite-name">
              <Building2 className="h-4 w-4 text-secondary" />
              {ctx?.worksite?.name || "—"}
              {ctx?.worksite?.code && (
                <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 font-mono text-[10px] tracking-wider text-white/70">
                  {ctx.worksite.code}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="font-display text-3xl font-semibold tabular-nums tracking-tight" data-testid="kiosk-clock">{timeStr}</div>
            <div className="text-xs text-white/50">{dateStr}</div>
          </div>
          <div className="flex flex-col gap-2">
            <Button variant="ghost" size="sm" onClick={onRefresh} className="h-9 gap-2 text-white/70 hover:bg-white/10 hover:text-white" data-testid="kiosk-refresh">
              <RefreshCcw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onUnpair} className="h-9 gap-2 text-white/40 hover:bg-white/10 hover:text-white" data-testid="kiosk-unpair">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

/* ---------- Roster ---------- */

function RosterView({ ctx, brand, onPick }) {
  const workers = ctx?.workers || [];
  const hasWorkers = workers.length > 0;

  return (
    <section className="pt-10" data-testid="kiosk-roster">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Tap your name to punch in or out</h1>
          <p className="mt-2 text-sm text-white/60">
            {workers.filter((w) => w.has_open_punch).length} of {workers.length} currently on the clock
          </p>
        </div>
      </div>

      {!hasWorkers && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center text-white/60" data-testid="kiosk-roster-empty">
          <UserRound className="mx-auto mb-3 h-8 w-8 text-white/40" />
          No workers assigned to this worksite yet. Ask an admin to add assignments in {brand}.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
        {workers.map((w) => (
          <button
            key={w.user_id}
            onClick={() => onPick(w)}
            disabled={!w.has_pin}
            className={
              "group relative flex h-40 flex-col items-center justify-center rounded-2xl border p-4 text-center transition " +
              (w.has_pin
                ? "cursor-pointer border-white/10 bg-white/[0.05] hover:border-secondary/60 hover:bg-white/[0.08]"
                : "cursor-not-allowed border-white/5 bg-white/[0.02] opacity-50")
            }
            data-testid={`kiosk-worker-tile-${w.user_id}`}
          >
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-secondary/20 font-display text-xl font-semibold tracking-wide text-white ring-2 ring-secondary/30 group-hover:ring-secondary">
              {w.initials}
            </div>
            <div className="line-clamp-2 text-sm font-semibold leading-tight">{w.full_name}</div>
            <div className="absolute right-3 top-3">
              {w.has_open_punch ? (
                <span className="flex items-center gap-1 rounded-full bg-success/20 px-2 py-0.5 text-[10px] font-medium text-success">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" /> On the clock
                </span>
              ) : !w.has_pin ? (
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/50">No PIN set</span>
              ) : null}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

/* ---------- PIN pad ---------- */

function PinView({ ctx, worker, onCancel, onSuccess }) {
  const length = ctx?.config?.pin_length || 6;
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [remaining, setRemaining] = useState(null);

  const submit = useCallback(async (candidate) => {
    setErr("");
    setBusy(true);
    // Handles success (200) and PIN business errors (401 invalid_pin, 423 locked).
    // Any other error falls through to the catch branch.
    const handleBody = (data) => {
      if (data?.error === "invalid_pin") {
        setPin("");
        setRemaining(data.attempts_remaining ?? null);
        setErr(
          data.attempts_remaining > 0
            ? `Wrong PIN. ${data.attempts_remaining} attempt${data.attempts_remaining === 1 ? "" : "s"} left.`
            : "Wrong PIN."
        );
        return true;
      }
      if (data?.error === "locked") {
        setLockedUntil(data.locked_until);
        setPin("");
        setErr("This account is temporarily locked.");
        return true;
      }
      return false;
    };
    try {
      const { data } = await kioskApi.post("/kiosk/pin-verify", {
        user_id: worker.user_id,
        pin: candidate,
      });
      if (handleBody(data)) return;
      onSuccess(data);
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      // Business errors now come back as 401 (invalid_pin) / 423 (locked).
      if ((status === 401 || status === 423) && handleBody(data)) return;
      setPin("");
      setErr(formatKioskError(e));
    } finally {
      setBusy(false);
    }
  }, [worker.user_id, onSuccess]);

  useEffect(() => {
    if (pin.length === length && !busy) submit(pin);
  }, [pin, length, busy, submit]);

  const press = (d) => {
    if (busy || lockedUntil) return;
    setPin((p) => (p.length >= length ? p : p + d));
  };
  const del = () => setPin((p) => p.slice(0, -1));

  const dots = Array.from({ length }, (_, i) => i < pin.length);

  return (
    <section className="pt-10" data-testid="kiosk-pin-view">
      <div className="mx-auto max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <Button variant="ghost" onClick={onCancel} className="gap-2 text-white/70 hover:bg-white/10 hover:text-white" data-testid="kiosk-pin-cancel">
            <ArrowLeft className="h-4 w-4" /> Not you?
          </Button>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur">
          <div className="mx-auto mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-secondary/20 font-display text-2xl font-semibold ring-2 ring-secondary/40">
            {worker.initials}
          </div>
          <h2 className="font-display text-2xl font-semibold" data-testid="kiosk-pin-worker-name">{worker.full_name}</h2>
          <p className="mt-1 text-sm text-white/60">Enter your {length}-digit PIN</p>

          <div className="my-6 flex justify-center gap-3" data-testid="kiosk-pin-dots">
            {dots.map((filled, i) => (
              <div
                key={i}
                className={
                  "h-4 w-4 rounded-full border-2 transition " +
                  (filled ? "border-secondary bg-secondary" : "border-white/25 bg-transparent")
                }
              />
            ))}
          </div>

          {err && !lockedUntil && (
            <div className="mb-4 text-sm font-medium text-red-300" data-testid="kiosk-pin-error">{err}</div>
          )}
          {lockedUntil && (
            <Alert className="mb-4 border-amber-400/40 bg-amber-500/10 text-amber-100" data-testid="kiosk-pin-locked">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Too many wrong attempts. Please ask an admin to unlock your account, or try again after{" "}
                {new Date(lockedUntil).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <PinKey key={n} label={String(n)} onPress={() => press(String(n))} testid={`kiosk-pin-key-${n}`} />
            ))}
            <div />
            <PinKey label="0" onPress={() => press("0")} testid="kiosk-pin-key-0" />
            <PinKey
              label={<Delete className="mx-auto h-6 w-6" />}
              onPress={del}
              testid="kiosk-pin-key-del"
              variant="ghost"
            />
          </div>
          {busy && <div className="mt-4 flex items-center justify-center gap-2 text-sm text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</div>}
        </div>
      </div>
    </section>
  );
}

function PinKey({ label, onPress, testid, variant }) {
  return (
    <button
      onClick={onPress}
      data-testid={testid}
      className={
        "h-16 rounded-xl border font-display text-2xl font-semibold transition active:scale-[0.98] " +
        (variant === "ghost"
          ? "border-white/5 bg-white/[0.03] text-white/70 hover:bg-white/10"
          : "border-white/10 bg-white/[0.06] text-white hover:border-secondary/50 hover:bg-white/[0.12]")
      }
    >
      {label}
    </button>
  );
}

/* ---------- Confirm (clock-in / clock-out / cross-site conflict) ---------- */

function ConfirmView({ ctx, worker, onBack, onDone }) {
  const state = worker.current_state?.status || "clocked_out";

  if (state === "clocked_in_here") {
    return <ConfirmClockOut ctx={ctx} worker={worker} onBack={onBack} onDone={(entry) => onDone("out", entry)} />;
  }
  if (state === "clocked_in_elsewhere") {
    return <ConfirmConflict ctx={ctx} worker={worker} onBack={onBack} onDone={(entry) => onDone("switch", entry)} />;
  }
  return <ConfirmClockIn ctx={ctx} worker={worker} onBack={onBack} onDone={(entry) => onDone("in", entry)} />;
}

function ConfirmShell({ children, onBack, workerName, subtitle }) {
  return (
    <section className="pt-10" data-testid="kiosk-confirm-view">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6">
          <Button variant="ghost" onClick={onBack} className="gap-2 text-white/70 hover:bg-white/10 hover:text-white" data-testid="kiosk-confirm-back">
            <ArrowLeft className="h-4 w-4" /> Cancel
          </Button>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur">
          <div className="mb-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/50">{workerName}</div>
            <h2 className="mt-1 font-display text-3xl font-semibold">{subtitle}</h2>
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

function ConfirmClockIn({ ctx, worker, onBack, onDone }) {
  const dims = ctx?.dimensions || [];
  const [values, setValues] = useState({});
  const [checklists, setChecklists] = useState([]);
  const [checked, setChecked] = useState({}); // {checklist_id: [item_id]}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Fetch worksite-scoped checklists whenever dim values change
  useEffect(() => {
    const worksite_id = ctx?.worksite?.id;
    if (!worksite_id) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ worksite_id });
        if (Object.keys(values).length) params.set("dimension_values", JSON.stringify(values));
        // Public kiosk cannot hit /time/resolve-checklists (auth required).
        // We rely on the confirm step to submit optimistically; checklists will surface after clock-in via /kiosk/session.
        // Keep this hook here for future extension — no-op for now.
        if (!cancelled) setChecklists([]);
      } catch {
        if (!cancelled) setChecklists([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ctx?.worksite?.id, values]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        worker_session_token: worker.token,
        dimension_values: values,
      };
      const checklist_checks = Object.entries(checked)
        .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
        .map(([checklist_id, item_ids]) => ({ checklist_id, item_ids }));
      if (checklist_checks.length) body.checklist_checks = checklist_checks;
      const { data } = await kioskApi.post("/kiosk/clock-in", body);
      onDone(data);
    } catch (e) {
      setErr(formatKioskError(e));
    } finally { setBusy(false); }
  };

  const dimReady = dims.every((d) => !d.required || values[d.key]);

  return (
    <ConfirmShell workerName={worker.full_name} subtitle="Ready to clock in?" onBack={onBack}>
      <div className="space-y-5">
        {dims.length === 0 && (
          <p className="text-sm text-white/60">Tap the big button below to start your shift at {ctx?.worksite?.name}.</p>
        )}
        {dims.map((d) => (
          <div key={d.id} className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-white/70">
              {d.label}{d.required && <span className="ml-1 text-red-300">*</span>}
            </label>
            <Select value={values[d.key] || ""} onValueChange={(v) => setValues((p) => ({ ...p, [d.key]: v }))}>
              <SelectTrigger className="h-14 rounded-xl border-white/15 bg-white/5 text-base text-white focus:ring-secondary" data-testid={`kiosk-dim-${d.key}`}>
                <SelectValue placeholder={`Select ${d.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {d.values.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.code} — {v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}

        {err && (
          <Alert variant="destructive" className="border-red-400/40 bg-red-500/10 text-red-100" data-testid="kiosk-confirm-error">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        <Button
          size="lg"
          onClick={submit}
          disabled={busy || !dimReady}
          className="h-16 w-full gap-2 rounded-xl bg-success text-lg text-white hover:bg-success/90"
          data-testid="kiosk-confirm-clock-in-btn"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />} Clock in at {ctx?.worksite?.name}
        </Button>
      </div>
    </ConfirmShell>
  );
}

function ConfirmClockOut({ ctx, worker, onBack, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ci = worker.current_state?.clock_in_at ? new Date(worker.current_state.clock_in_at).getTime() : now;
  const elapsed = Math.max(0, Math.floor((now - ci) / 1000));
  const hh = Math.floor(elapsed / 3600);
  const mm = Math.floor((elapsed % 3600) / 60);
  const pad = (n) => String(n).padStart(2, "0");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await kioskApi.post("/kiosk/clock-out", { worker_session_token: worker.token });
      onDone(data);
    } catch (e) {
      setErr(formatKioskError(e));
    } finally { setBusy(false); }
  };

  return (
    <ConfirmShell workerName={worker.full_name} subtitle="Ready to clock out?" onBack={onBack}>
      <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center">
        <div className="text-xs uppercase tracking-widest text-white/50">Elapsed on shift</div>
        <div className="mt-1 font-display text-5xl font-semibold tabular-nums tracking-tight" data-testid="kiosk-elapsed">
          {hh > 0 ? `${pad(hh)}:${pad(mm)}` : `${pad(mm)}:00`}
        </div>
        <div className="mt-2 text-sm text-white/60">
          Started {new Date(worker.current_state?.clock_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · at {ctx?.worksite?.name}
        </div>
      </div>

      {err && <Alert variant="destructive" className="mb-4 border-red-400/40 bg-red-500/10 text-red-100"><AlertDescription>{err}</AlertDescription></Alert>}

      <Button
        size="lg"
        onClick={submit}
        disabled={busy}
        className="h-16 w-full gap-2 rounded-xl bg-red-500 text-lg text-white hover:bg-red-500/90"
        data-testid="kiosk-confirm-clock-out-btn"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Square className="h-5 w-5" />} Clock out
      </Button>
    </ConfirmShell>
  );
}

function ConfirmConflict({ ctx, worker, onBack, onDone }) {
  const other = worker.current_state?.open_worksite_name || "another worksite";
  const dims = ctx?.dimensions || [];
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await kioskApi.post("/kiosk/clock-in", {
        worker_session_token: worker.token,
        dimension_values: values,
        force_close_other: true,
      });
      onDone(data);
    } catch (e) {
      setErr(formatKioskError(e));
    } finally { setBusy(false); }
  };

  const dimReady = dims.every((d) => !d.required || values[d.key]);

  return (
    <ConfirmShell workerName={worker.full_name} subtitle="You're clocked in elsewhere" onBack={onBack}>
      <Alert className="mb-5 border-amber-400/40 bg-amber-500/10 text-amber-100" data-testid="kiosk-conflict-alert">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          You have an open punch at <span className="font-semibold">{other}</span>. Continuing here will close that
          punch and start a new one at <span className="font-semibold">{ctx?.worksite?.name}</span>.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {dims.map((d) => (
          <div key={d.id} className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-white/70">
              {d.label}{d.required && <span className="ml-1 text-red-300">*</span>}
            </label>
            <Select value={values[d.key] || ""} onValueChange={(v) => setValues((p) => ({ ...p, [d.key]: v }))}>
              <SelectTrigger className="h-14 rounded-xl border-white/15 bg-white/5 text-base text-white focus:ring-secondary" data-testid={`kiosk-conflict-dim-${d.key}`}>
                <SelectValue placeholder={`Select ${d.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {d.values.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.code} — {v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      {err && <Alert variant="destructive" className="mt-4 border-red-400/40 bg-red-500/10 text-red-100"><AlertDescription>{err}</AlertDescription></Alert>}

      <Button
        size="lg"
        onClick={submit}
        disabled={busy || !dimReady}
        className="mt-6 h-16 w-full gap-2 rounded-xl bg-secondary text-lg text-white hover:bg-secondary/90"
        data-testid="kiosk-confirm-force-close-btn"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
        Close {other} punch & clock in here
      </Button>
    </ConfirmShell>
  );
}

/* ---------- Result (auto-return in 10s) ---------- */

function ResultView({ result, worker, onDone }) {
  const [count, setCount] = useState(10);
  useEffect(() => {
    if (count <= 0) { onDone(); return; }
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count, onDone]);

  const meta = useMemo(() => {
    if (result.kind === "in") return { icon: CheckCircle2, tone: "text-success", tint: "bg-success/15", title: "You're clocked in", copy: "Have a great shift." };
    if (result.kind === "switch") return { icon: CheckCircle2, tone: "text-success", tint: "bg-success/15", title: "Switched worksites", copy: "Previous punch was closed and a new one started." };
    return { icon: XCircle, tone: "text-red-300", tint: "bg-red-500/15", title: "You're clocked out", copy: "See you next shift." };
  }, [result.kind]);

  const Icon = meta.icon;

  const shiftStr = () => {
    if (result.kind === "out" && result.entry?.duration_minutes != null) {
      const m = result.entry.duration_minutes;
      const h = Math.floor(m / 60);
      const rem = m % 60;
      return h > 0 ? `${h}h ${rem}m recorded` : `${rem}m recorded`;
    }
    return null;
  };

  return (
    <section className="pt-10" data-testid="kiosk-result-view">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-10 text-center backdrop-blur">
          <div className={`mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full ${meta.tint}`}>
            <Icon className={`h-14 w-14 ${meta.tone}`} />
          </div>
          <h2 className="font-display text-4xl font-semibold" data-testid="kiosk-result-title">{meta.title}</h2>
          <div className="mt-2 text-lg text-white/80">{worker?.full_name}</div>
          <p className="mt-1 text-sm text-white/60">{meta.copy}</p>
          {shiftStr() && (
            <Badge className="mt-4 rounded-full bg-secondary/20 text-secondary" data-testid="kiosk-result-hours">
              <Clock className="mr-1 h-3.5 w-3.5" /> {shiftStr()}
            </Badge>
          )}
          <div className="mt-8">
            <Button variant="outline" onClick={onDone} className="rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="kiosk-result-done">
              Done ({count}s)
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
